/** @file pivotKindOf: what a part is for slot.fitPivot (limb, hanging, pinned, torso, head) (CR-012). */
import type { ImpostorDrawable } from '@zilo/pose3d-format/types/drawable';
import type { FacetEntry } from '@zilo/pose3d-format/types/facet';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import type { PivotPartKind } from '../../types/puppetInputs';

const HEAD_NAME = /head|face|hair|hat|helm|eye|mouth/i;
const TORSO_NAME = /torso|body|chest|pelvis|hip/i;

/** The drawn screen angle points up the image (|angle - 180| < 90): the joint of a two-point part is at the top. */
const pointsUp = (angle: number | undefined): boolean => angle !== undefined && Math.cos((angle * Math.PI) / 180) < -1e-9;

/**
 * Two-point (`rotate` screen | parent, `cut` not none): `hanging` when the tile's drawn screen angle points up (a tail
 * or cape hanging from its root), else `limb`; `rotate: 'screen'` + `cut: 'none'`: `torso`; `rotate: 'parent'` +
 * `cut: 'none'`: `pinned`; a non-rotating entry: `head` for a `lookAt` node or a head-like drawable or node name,
 * `torso` for a torso-like name, else `pinned`.
 */
export function pivotKindOf(puppet: Pick<Puppet, 'nodes'>, drawable: Pick<ImpostorDrawable, 'id' | 'node'>, entry: Pick<FacetEntry, 'rotate' | 'cut'>, angle: number | undefined): PivotPartKind {
  const rotating = entry.rotate === 'screen' || entry.rotate === 'parent';
  if (rotating && entry.cut !== 'none') return pointsUp(angle) ? 'hanging' : 'limb';
  if (rotating) return entry.rotate === 'screen' ? 'torso' : 'pinned';
  return stillKindOf(puppet, drawable);
}

/** A non-rotating part: `head` (looks at things, or named like a head), `torso` (named like one) or `pinned`. */
function stillKindOf(puppet: Pick<Puppet, 'nodes'>, drawable: Pick<ImpostorDrawable, 'id' | 'node'>): PivotPartKind {
  const names = `${drawable.id} ${drawable.node}`;
  if (puppet.nodes[drawable.node]?.lookAt === true || HEAD_NAME.test(names)) return 'head';
  return TORSO_NAME.test(names) ? 'torso' : 'pinned';
}
